import React, { useState, useCallback, useRef, useMemo, useEffect } from "react";
import { Drawer, Button } from "antd";
import { MessageOutlined, MenuOutlined, CloseOutlined } from "@ant-design/icons";
import { useQuery, useQueryClient } from "react-query";
import { toast } from "sonner";

import { useSendMessage, useSendImage, useDeleteMessage } from "./hooks";
import { WebSocketManager, wsLogger } from "./webSocket";
import { QUERY_KEYS, fetchChats, fetchChannels, fetchChatMessages, formatTime, getSenderType, fileToBase64, playNotificationSound, unlockAudioForNotifications } from "./utils";
import UserList from "./UserList";
import ChatWindow from "./ChatWindow";
import ChatInfo from "./ChatInfo";


const AllChatsConnection = React.memo(({ token }) => {
  const handlersRef = useRef(null);
  const isConnectingRef = useRef(false);
  const wsManagerRef = useRef(WebSocketManager.getInstance());
  const lastTokenRef = useRef(null);

  useEffect(() => {
    const wsManager = wsManagerRef.current;

    if (!token) {
      return;
    }

    if (lastTokenRef.current === token && isConnectingRef.current) {
      return;
    }

    if (lastTokenRef.current !== token) {
      isConnectingRef.current = false;
      lastTokenRef.current = token;
    }

    if (isConnectingRef.current) {
      return;
    }

    if (!handlersRef.current) {
      handlersRef.current = {
        onOpen: () => {
          isConnectingRef.current = false;
        },
        onMessage: (data) => {
          const handler = wsManager.createAllChatsHandler();
          if (handler) handler(data);
        },
        onClose: (event) => {
          isConnectingRef.current = false;
        },
        onError: (error) => {
          isConnectingRef.current = false;
        }
      };
    }

    isConnectingRef.current = true;
    wsManager.connectToAllChats(token, handlersRef.current);

    return () => {
      if (lastTokenRef.current !== token) {
        isConnectingRef.current = false;
        wsManager.disconnectAllChats();
      }
    };
  }, [token]); 

  return null;
});


function ChatsComponent({ query }) {
  const queryClient = useQueryClient();
  const wsManagerRef = useRef(WebSocketManager.getInstance());
  
  const [searchText, setSearchText] = useState("");
  const [selectedChat, setSelectedChat] = useState(null);
  const [activeTab] = useState("all");
  const [showChatInfo, setShowChatInfo] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [userListVisible, setUserListVisible] = useState(false);
  const [chatInfoVisible, setChatInfoVisible] = useState(false);
  
  const [filters, setFilters] = useState({
        sort_by: "last_message_time",
        sort_order: "desc",
    limit: 100,
    search: ""
  });

  const messagesEndRef = useRef(null);
  const [typingStatus, setTypingStatus] = useState({});
  const [onlineStatus, setOnlineStatus] = useState({});
  const [lastMessageUpdateVersion, setLastMessageUpdateVersion] = useState(0);
  const typingTimerRef = useRef(null);
  const lastTypingSentRef = useRef({});
  const wsManager = wsManagerRef.current;

  const handleNewMessage = useCallback((message) => {
    if (!message?.chat_id) return;
    const token = query?.token;
    if (!token) return;

    const senderUpper = (message.sender_type || "").toUpperCase();
    const isClientMessage = senderUpper === "CLIENT";
    const isOwnMessage = senderUpper === (getSenderType() || "").toUpperCase();

    if (isClientMessage && !isOwnMessage) {
      playNotificationSound();
    }

    queryClient.setQueryData(
      QUERY_KEYS.CHAT_MESSAGES(message.chat_id, token),
      (oldData = []) => {
        const messageId = message.id ?? message.message_id;
        const exists = oldData.some(msg =>
          (msg.id ?? msg.message_id) === messageId ||
          (msg.sender_type === message.sender_type &&
            msg.content === message.content &&
            Math.abs(new Date(msg.created_at) - new Date(message.created_at)) < 1000)
        );
        if (exists) return oldData;

        let data = oldData;
        if (isOwnMessage) {
          const tempIndex = data.findIndex(msg =>
            String(msg.id || msg.message_id || "").startsWith("temp_")
          );
          if (tempIndex !== -1) {
            data = [...data.slice(0, tempIndex), ...data.slice(tempIndex + 1)];
          }
        }
        return [...data, message];
      }
    );

    queryClient.setQueryData(
      QUERY_KEYS.CHATS(token, filters),
      (oldData = []) => {
        return oldData.map(chat => {
          if (chat.id === message.chat_id) {
            return {
              ...chat,
              last_message_preview: message.content,
              last_message_time: message.created_at,
              updated_at: message.created_at,
              unread_count: message.sender_type !== getSenderType() &&
                message.chat_id !== selectedChat?.id
                  ? (chat.unread_count || 0) + 1
                  : chat.unread_count || 0
            };
          }
          return chat;
        });
      }
    );

    queryClient.invalidateQueries({ queryKey: ["chats", token] });
  }, [query?.token, queryClient, filters, selectedChat]);

  const handleTypingUpdate = useCallback((chatId, typingData) => {
    setTypingStatus(prev => ({ ...prev, [chatId]: typingData }));
  }, []);

  const handleOnlineUpdate = useCallback((chatId, onlineData) => {
    setOnlineStatus(prev => ({ ...prev, [chatId]: onlineData }));
  }, []);

  const handleChatUpdated = useCallback((chatId) => {
    const token = query?.token;
    if (!token) return;
    queryClient.invalidateQueries({ queryKey: ['chats', token] });
    queryClient.refetchQueries({ queryKey: ['chats', token] });
  }, [query?.token, queryClient]);

  const handleLastMessageUpdate = useCallback(() => {
    setLastMessageUpdateVersion((v) => v + 1);
  }, []);

  const handleNewMessageRef = useRef(handleNewMessage);
  const handleChatUpdatedRef = useRef(handleChatUpdated);
  const handleLastMessageUpdateRef = useRef(handleLastMessageUpdate);
  handleNewMessageRef.current = handleNewMessage;
  handleChatUpdatedRef.current = handleChatUpdated;
  handleLastMessageUpdateRef.current = handleLastMessageUpdate;

  useEffect(() => {
    wsManager.setCallbacks({
      onTypingUpdate: handleTypingUpdate,
      onOnlineUpdate: handleOnlineUpdate,
      onNewMessage: (message) => handleNewMessageRef.current?.(message),
      onChatUpdated: (chatId) => handleChatUpdatedRef.current?.(chatId),
      onLastMessageUpdate: () => handleLastMessageUpdateRef.current?.()
    });

    return () => {
      if (typingTimerRef.current) {
        clearTimeout(typingTimerRef.current);
        typingTimerRef.current = null;
      }
    };
  }, [handleTypingUpdate, handleOnlineUpdate, handleNewMessage, handleChatUpdated, handleLastMessageUpdate, wsManager]);


  useEffect(() => {
    const unlock = () => unlockAudioForNotifications();
    document.body.addEventListener("click", unlock, { once: true });
    document.body.addEventListener("touchstart", unlock, { once: true });
    document.body.addEventListener("keydown", unlock, { once: true });
  }, []);

  useEffect(() => {
    if (!selectedChat?.id || !query?.token) {
      if (selectedChat?.id) {
        wsManager.disconnectFromChat(selectedChat.id);
      }
      return;
    }
    const chatId = selectedChat.id;
    const lastTypingSent = lastTypingSentRef.current;
    
    const chatHandlers = {
      onMessage: (data) => wsManager.createChatHandler(chatId)(data),
      onOpen: () => wsLogger.info(`WebSocket чата ${chatId} открыт`),
      onClose: (event) => wsLogger.info(`WebSocket чата ${chatId} закрыт`),
      onError: (error) => wsLogger.error(error, `WebSocket чата ${chatId}`)
    };

    wsManager.connectToChat(chatId, query.token, chatHandlers);

    return () => {
      wsManager.disconnectFromChat(chatId);
      if (lastTypingSent[chatId]) {
        wsManager.sendTypingIndicator(chatId, false, getSenderType());
        delete lastTypingSent[chatId];
      }
    };
  }, [selectedChat?.id, query?.token, wsManager]);

  
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) setUserListVisible(true);
    };

    checkMobile();
    const resizeHandler = () => checkMobile();
    window.addEventListener('resize', resizeHandler);
    return () => window.removeEventListener('resize', resizeHandler);
  }, []);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible' || !query?.token) return;
      queryClient.invalidateQueries({ queryKey: ['chats', query.token] });
      queryClient.refetchQueries({ queryKey: ['chats', query.token] });
      if (selectedChat?.id) {
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.CHAT_MESSAGES(selectedChat.id, query.token),
        });
        queryClient.refetchQueries({
          queryKey: QUERY_KEYS.CHAT_MESSAGES(selectedChat.id, query.token),
        });
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [query?.token, queryClient, selectedChat?.id]);

  const { data: chats = [], isLoading: chatsLoading } = useQuery(
    QUERY_KEYS.CHATS(query?.token, filters),
    fetchChats,
    { enabled: !!query?.token, staleTime: 30000, cacheTime: 60000 }
  );

  const { data: channels = [], isLoading: channelsLoading } = useQuery(
    QUERY_KEYS.CHANNELS(query?.token),
    fetchChannels,
    { enabled: !!query?.token, staleTime: 60000 }
  );

  const { data: messages = [], isLoading: messagesLoading } = useQuery(
    QUERY_KEYS.CHAT_MESSAGES(selectedChat?.id, query?.token),
    fetchChatMessages,
    { enabled: !!selectedChat?.id && !!query?.token, staleTime: 10000 }
  );

  const sendMessageMutation = useSendMessage();
  const sendImageMutation = useSendImage();
  const deleteMessageMutation = useDeleteMessage();

  const getCurrentWebSocket = useCallback(() => {
    if (!selectedChat?.id) return null;
    const socket = wsManager.getSocket(selectedChat.id);
    return socket?.ws || null;
  }, [selectedChat?.id, wsManager]);

  const selectedChatTyping = selectedChat?.id
    ? wsManager.getTypingStatus(selectedChat.id)
    : null;

  const selectedChatOnline = selectedChat?.id
    ? wsManager.getOnlineStatus(selectedChat.id)
    : false;

  const enhancedChats = useMemo(() => {
    if (!chats.length) return [];
    void lastMessageUpdateVersion;
    return chats.map(chat => {
      const enhancedChat = { ...chat };
      const lastMessageUpdate = wsManager.getLastMessageUpdates(chat.id);
      
      if (lastMessageUpdate) {
        enhancedChat.last_message_preview = lastMessageUpdate.last_message;
        enhancedChat.last_message_time = lastMessageUpdate.last_message_time;
        enhancedChat.updated_at = lastMessageUpdate.last_message_time;
      }
      
      const typing = wsManager.getTypingStatus(chat.id);
      if (typing?.isTyping) {
        enhancedChat.last_message_preview = "Печатает...";
        enhancedChat.last_message_time = new Date().toISOString();
        enhancedChat.updated_at = new Date().toISOString();
      }
      
      return enhancedChat;
    });
  }, [chats, wsManager, lastMessageUpdateVersion]);

  const filteredChats = useMemo(() => {
    if (!enhancedChats.length) return [];
    
    let filtered = [...enhancedChats];
    
    if (activeTab === 'unread') {
      filtered = filtered.filter(chat => chat.unread_count > 0);
    }
    
    filtered.sort((a, b) => {
      const timeA = new Date(a.last_message_time || a.created_at);
      const timeB = new Date(b.last_message_time || b.created_at);
      return timeB - timeA;
    });

    return filtered;
  }, [enhancedChats, activeTab]);

  const handleSendMessage = useCallback(async (content) => {
    if (!content?.trim() || !selectedChat || !query?.token) return;
    
    try {
      await sendMessageMutation.mutateAsync({
        chatId: selectedChat.id,
        content: content.trim(),
        token: query.token,
        websocket: getCurrentWebSocket(),
      });
      
      if (typingTimerRef.current) {
        clearTimeout(typingTimerRef.current);
        typingTimerRef.current = null;
      }
      wsManager.sendTypingIndicator(selectedChat.id, false, getSenderType());
      delete lastTypingSentRef.current[selectedChat.id];
      
      return true;
    } catch (error) {
      console.error('Ошибка отправки сообщения:', error);
      toast.error('Ошибка отправки сообщения');
      return false;
    }
  }, [selectedChat, query?.token, sendMessageMutation, getCurrentWebSocket, wsManager]);

  const handleSendImage = useCallback(async (file) => {
    if (!file || !selectedChat || !query?.token) return;
    
    try {
      const base64Data = await fileToBase64(file);
      await sendImageMutation.mutateAsync({
        chatId: selectedChat.id,
        base64Data: base64Data,
        token: query.token,
        websocket: getCurrentWebSocket()
      });
      
      if (typingTimerRef.current) {
        clearTimeout(typingTimerRef.current);
        typingTimerRef.current = null;
      }
      wsManager.sendTypingIndicator(selectedChat.id, false, getSenderType());
      delete lastTypingSentRef.current[selectedChat.id];
      
      return true;
    } catch (error) {
      console.error('Ошибка отправки изображения:', error);
      toast.error('Ошибка отправки изображения');
      return false;
    }
  }, [selectedChat, query?.token, sendImageMutation, getCurrentWebSocket, wsManager]);

  const handleSendTypingIndicator = useCallback((isTyping) => {
    if (!selectedChat?.id) return;
    
    if (typingTimerRef.current) {
      clearTimeout(typingTimerRef.current);
      typingTimerRef.current = null;
    }
    
    if (isTyping) {
      const chatId = selectedChat.id;
      const now = Date.now();
      const lastSent = lastTypingSentRef.current[chatId];
      
      if (!lastSent || (now - lastSent) > 1000) {
        wsManager.sendTypingIndicator(chatId, true, getSenderType());
        lastTypingSentRef.current[chatId] = now;
      }
      
      typingTimerRef.current = setTimeout(() => {
        wsManager.sendTypingIndicator(chatId, false, getSenderType());
        delete lastTypingSentRef.current[chatId];
        typingTimerRef.current = null;
      }, 500);
    } else {
      wsManager.sendTypingIndicator(selectedChat.id, false, getSenderType());
      delete lastTypingSentRef.current[selectedChat.id];
    }
  }, [selectedChat?.id, wsManager]);

  const handleSelectChat = useCallback((chat) => {
    setSelectedChat(chat);
    queryClient.setQueryData(
      QUERY_KEYS.CHATS(query.token, filters),
      (oldData = []) => oldData.map(c => 
        c.id === chat.id ? { ...c, unread_count: 0 } : c
      )
    );
  }, [query.token, filters, queryClient]);

  const handleDeleteMessage = useCallback(async (message) => {
    if (!message?.id) return;
    
    try {
      await deleteMessageMutation.mutateAsync(message.id);
      toast.success('Сообщение удалено');
    } catch (error) {
      console.error('Ошибка удаления сообщения:', error);
      toast.error('Ошибка удаления сообщения');
    }
  }, [deleteMessageMutation]);

  const handleAvatarClick = useCallback(() => {
    setShowChatInfo((prev) => !prev);
    if (isMobile) {
      setChatInfoVisible((prev) => !prev);
    }
  }, [isMobile]);

  const handleBackToList = useCallback(() => {
    setSelectedChat(null);
    if (isMobile) setUserListVisible(true);
  }, [isMobile]);

  const handleSearch = useCallback((searchValue) => {
    setSearchText(searchValue);
    setFilters(prev => ({ ...prev, search: searchValue }));
  }, []);

  const handleApplyFilters = useCallback((newFilters) => {
    if (Object.keys(newFilters).length === 0) {
      setFilters({ sort_by: "last_message_time", sort_order: "desc", limit: 100, search: "" });
    } else {
      setFilters(prev => {
        const updated = { ...prev };
        Object.keys(newFilters).forEach(key => {
          if (newFilters[key] === undefined || newFilters[key] === '' || newFilters[key] === null) {
            delete updated[key];
          } else {
            updated[key] = newFilters[key];
          }
        });
        return updated;
      });
    }
  }, []);

  const containerStyle = { height: "100vh", padding: 10, display: "flex", position: "relative", overflow: "hidden" };
  const isLoading = chatsLoading || channelsLoading;

  return (
    <>
      <AllChatsConnection token={query?.token} />
      
      <div style={containerStyle}>
        {!isMobile && (
          <>
            <div style={{ minWidth: "400px", maxWidth: "400px", height: "100%", background: "#fff", borderRight: "1px solid #f0f0f0", overflowY: "auto", flexShrink: 0, scrollbarGutter: "stable" }}>
              <UserList
                chats={filteredChats}
                loading={isLoading}
                searchText={searchText}
                setSearchText={handleSearch}
                channels={channels}
                channelsLoading={channelsLoading}
                selectedChat={selectedChat}
                onSelectChat={handleSelectChat}
                formatTime={formatTime}
                token={query?.token}
                onApplyFilters={handleApplyFilters}
                currentFilters={filters}
                typingStatus={typingStatus}
                onlineStatus={onlineStatus}
          />
        </div>

            <div style={{ flex: 1, height: "100%", background: "#fff", display: "flex", flexDirection: "column", minWidth: "400px" }}>
              {selectedChat ? (
                <ChatWindow
                  selectedChat={selectedChat}
                  messages={messages}
                  messageLoading={messagesLoading}
                  messagesEndRef={messagesEndRef}
                  formatTime={formatTime}
                  onAvatarClick={handleAvatarClick}
                  token={query?.token}
                  onDeleteMessage={handleDeleteMessage}
                  onSendMessage={handleSendMessage}
                  onSendImage={handleSendImage}
                  onSendTypingIndicator={handleSendTypingIndicator}
                  websocket={getCurrentWebSocket()}
                  typingStatus={selectedChatTyping}
                  onlineStatus={selectedChatOnline}
                  isMobile={isMobile}
                  onBackToList={handleBackToList}
                />
              ) : (
                <div style={{ height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: "#bfbfbf" }}>
                  <MessageOutlined style={{ fontSize: 64, marginBottom: 16 }} />
                  <div>Выберите чат</div>
            </div>
              )}
            </div>
            
            {selectedChat && showChatInfo && (
              <div style={{ minWidth: "400px", maxWidth: "400px", height: "100%", background: "#fff", borderLeft: "1px solid #f0f0f0", overflowY: "auto", flexShrink: 0 }}>
                <ChatInfo selectedChat={selectedChat} formatTime={formatTime} token={query?.token} />
              </div>
            )}
          </>
        )}

        {isMobile && (
          <>
            <Drawer title="Чаты" placement="left" onClose={() => setUserListVisible(false)} open={userListVisible} width="100%" bodyStyle={{ padding: 0 }} closeIcon={<CloseOutlined />}>
              <UserList
                chats={filteredChats}
                loading={isLoading}
                searchText={searchText}
                setSearchText={handleSearch}
                channels={channels}
                channelsLoading={channelsLoading}
                selectedChat={selectedChat}
                onSelectChat={handleSelectChat}
                formatTime={formatTime}
                token={query?.token}
                onApplyFilters={handleApplyFilters}
                currentFilters={filters}
                typingStatus={typingStatus}
                onlineStatus={onlineStatus}
              />
            </Drawer>

            <div style={{ width: "100%", height: "100%", background: "#fff", display: "flex", flexDirection: "column" }}>
              {selectedChat ? (
                <>
                  <div style={{ padding: "12px 16px", borderBottom: "1px solid #f0f0f0", display: "flex", alignItems: "center", justifyContent: "space-between", background: "#fff", zIndex: 10 }}></div>
                  <ChatWindow
                    selectedChat={selectedChat}
                    messages={messages}
                    messageLoading={messagesLoading}
                    messagesEndRef={messagesEndRef}
                    formatTime={formatTime}
                    onAvatarClick={handleAvatarClick}
                    token={query?.token}
                    onDeleteMessage={handleDeleteMessage}
                    onSendMessage={handleSendMessage}
                    onSendImage={handleSendImage}
                    onSendTypingIndicator={handleSendTypingIndicator}
                    websocket={getCurrentWebSocket()}
                    typingStatus={selectedChatTyping}
                    onlineStatus={selectedChatOnline}
                    isMobile={isMobile}
                    onBackToList={handleBackToList}
                  />
                </>
              ) : (
                <div style={{ height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: "#bfbfbf", padding: "20px" }}>
                  <MessageOutlined style={{ fontSize: 64, marginBottom: 16 }} />
                  <div style={{ marginBottom: 24, fontSize: "18px", textAlign: "center" }}>Выберите чат для начала общения</div>
                  <Button type="primary" icon={<MenuOutlined />} onClick={() => setUserListVisible(true)}>Открыть список чатов</Button>
                </div>
          )}
            </div>

            <Drawer title="Информация о чате" placement="right" onClose={() => setChatInfoVisible(false)} open={chatInfoVisible} width="100%" minWidth="300px" bodyStyle={{ padding: 0 }}>
              {selectedChat && <ChatInfo selectedChat={selectedChat} formatTime={formatTime} token={query?.token} />}
            </Drawer>
          </>
        )}

        {isMobile && selectedChat && !userListVisible && (
          <Button type="primary" shape="circle" icon={<MenuOutlined />} style={{ position: "fixed", top: "10px", left: "16px", width: "25px", height: "33px", zIndex: 1000 }} onClick={() => setUserListVisible(true)} />
        )}
    </div>
    </>
  );
}


export default React.memo(ChatsComponent);
